import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface IndivisionUnit {
  id: string
  unit_number: string
  owners: Array<{
    id: string
    first_name: string
    last_name: string
  }>
  common_rep_owner_id: string | null
  common_rep_contact_id: string | null
  common_rep_owner?: {
    id: string
    first_name: string
    last_name: string
    email: string | null
    notification_preference: string
  } | null
  common_rep_contact?: {
    id: string
    first_name: string
    last_name: string
    email: string | null
    notification_preference: string
  } | null
}

export interface IndivisionAlerts {
  indivisionUnits: IndivisionUnit[]
  unitsWithoutMandataire: IndivisionUnit[]
  unitsWithMandataire: IndivisionUnit[]
  hasAlerts: boolean
}

/**
 * Hook pour récupérer les alertes d'indivision pour une résidence
 * - Identifie les lots en indivision (>1 propriétaire actif)
 * - Vérifie si un mandataire est désigné
 */
export function useIndivisionAlerts(residenceId: string | undefined) {
  return useQuery({
    queryKey: ['indivision-alerts', residenceId],
    queryFn: async (): Promise<IndivisionAlerts> => {
      if (!residenceId) throw new Error('Residence ID is required')

      // Récupérer tous les lots avec leurs propriétaires actifs et mandataires
      const { data: units, error } = await supabase
        .from('units')
        .select(`
          id,
          unit_number,
          common_rep_owner_id,
          common_rep_contact_id,
          common_rep_owner:owners!units_common_rep_owner_id_fkey(id, first_name, last_name, email, notification_preference),
          common_rep_contact:external_contacts!units_common_rep_contact_id_fkey(id, first_name, last_name, email, notification_preference),
          ownerships!inner(
            id,
            end_date,
            owners(id, first_name, last_name)
          )
        `)
        .eq('residence_id', residenceId)
        .is('deleted_at', null)

      if (error) throw error

      // Filtrer pour ne garder que les propriétaires actifs (end_date IS NULL)
      const unitsWithActiveOwners = (units || []).map((unit) => {
        const activeOwnerships = (unit.ownerships || []).filter((o: { end_date: string | null }) => o.end_date === null)
        return {
          ...unit,
          owners: activeOwnerships.map((o: { owners: { id: string; first_name: string; last_name: string } }) => o.owners),
        }
      })

      // Identifier les lots en indivision (>1 propriétaire actif)
      const indivisionUnits = unitsWithActiveOwners
        .filter((unit) => unit.owners.length > 1)
        .map((unit) => ({
          id: unit.id,
          unit_number: unit.unit_number,
          owners: unit.owners,
          common_rep_owner_id: unit.common_rep_owner_id,
          common_rep_contact_id: unit.common_rep_contact_id,
          common_rep_owner: unit.common_rep_owner as IndivisionUnit['common_rep_owner'],
          common_rep_contact: unit.common_rep_contact as IndivisionUnit['common_rep_contact'],
        }))

      // Séparer ceux avec et sans mandataire
      const unitsWithoutMandataire = indivisionUnits.filter(
        (u) => !u.common_rep_owner_id && !u.common_rep_contact_id
      )
      const unitsWithMandataire = indivisionUnits.filter(
        (u) => u.common_rep_owner_id || u.common_rep_contact_id
      )

      return {
        indivisionUnits,
        unitsWithoutMandataire,
        unitsWithMandataire,
        hasAlerts: unitsWithoutMandataire.length > 0,
      }
    },
    enabled: !!residenceId,
  })
}

/**
 * Récupère le destinataire de convocation pour un lot
 * - Si le lot est en indivision avec mandataire: retourne le mandataire
 * - Sinon: retourne le premier propriétaire
 */
export function getConvocationRecipient(
  unit: IndivisionUnit
): { type: 'owner' | 'contact'; id: string; name: string; email: string | null } | null {
  if (unit.common_rep_owner_id && unit.common_rep_owner) {
    return {
      type: 'owner',
      id: unit.common_rep_owner.id,
      name: `${unit.common_rep_owner.first_name} ${unit.common_rep_owner.last_name}`,
      email: unit.common_rep_owner.email,
    }
  }

  if (unit.common_rep_contact_id && unit.common_rep_contact) {
    return {
      type: 'contact',
      id: unit.common_rep_contact.id,
      name: `${unit.common_rep_contact.first_name} ${unit.common_rep_contact.last_name}`,
      email: unit.common_rep_contact.email,
    }
  }

  // Pas de mandataire, utiliser le premier propriétaire
  const firstOwner = unit.owners[0]
  if (firstOwner) {
    return {
      type: 'owner',
      id: firstOwner.id,
      name: `${firstOwner.first_name} ${firstOwner.last_name}`,
      email: null, // On n'a pas l'email ici, il faudrait le récupérer
    }
  }

  return null
}
